import { Card } from "../types/v2/Card";
import { Job } from "../types/v2/Job";

export type JobState = "pending" | "approved" | "completed" | "declined";

/**
 * Completed is checked before authorized: a completed job that was
 * authorized-then-finished must not fall through to "declined" just
 * because upstream sync timing left `authorized` stale.
 */
export function deriveJobState(job: Job): JobState {
  if (job.completedDate) return "completed";
  if (job.authorizedDate == null) return "pending";
  return job.authorized ? "approved" : "declined";
}

/**
 * loggedHours is only backfilled to laborHours by the Tekmetric converter
 * once a job completes (see converter/index.ts) — while a job is open,
 * loggedHours is the raw partial clocked time (e.g. 1h clocked so far on a
 * 6h-estimated job), which is not the figure this app means by "hours" for
 * an open job elsewhere (see calculateApprovedHours in repositories/cards.ts,
 * which sums laborHours). So completed jobs prefer loggedHours (the actual
 * time worked); open jobs prefer laborHours (the estimate).
 */
export function getJobHours(job: Job): number {
  if (job.completedDate) return job.loggedHours ?? job.laborHours ?? 0;
  return job.laborHours ?? job.loggedHours ?? 0;
}

export type JobEntry = {
  id: string;
  employeeId: string;
  cardId: string;
  cardNumber: string | null;
  jobName: string | null;
  state: JobState;
  hours: number;
};

/**
 * Decomposes one card into per-employee, per-job JobEntry rows — the
 * shared primitive behind both the parent table's aggregation
 * (dispatchCalculations.ts) and the per-employee drill-down
 * (dispatchJobRows.ts), so the state-bucketing logic (including the
 * no-jobs[] fallback interpretation) lives in exactly one place.
 *
 * A card with no itemized jobs[] (some Shopware/manual cards) has no
 * per-job authorized/authorizedDate to derive Pending/Declined from, so
 * it falls back to a single synthetic entry bucketed by the one state
 * signal that does exist at the card level: card.completedDate.
 */
export function getEmployeeJobEntriesFromCard(card: Card): JobEntry[] {
  if (!card.jobs || card.jobs.length === 0) {
    if (!card.technicianId || !card.totalLaborHours) return [];
    const isCompleted = !!card.completedDate;
    return [
      {
        id: `${card.id}-fallback`,
        employeeId: card.technicianId,
        cardId: card.id,
        cardNumber: card.cardNumber ?? null,
        jobName: null,
        state: isCompleted ? "completed" : "approved",
        hours: isCompleted
          ? (card.totalLaborHours.loggedLabor ??
            card.totalLaborHours.estimatedLabor ??
            0)
          : (card.totalLaborHours.estimatedLabor ??
            card.totalLaborHours.loggedLabor ??
            0),
      },
    ];
  }

  return card.jobs
    .filter((job): job is Job & { technicianId: string } => !!job.technicianId)
    .map((job, index) => ({
      id: `${card.id}-job-${index}`,
      employeeId: job.technicianId,
      cardId: card.id,
      cardNumber: card.cardNumber ?? null,
      jobName: job.name,
      state: deriveJobState(job),
      hours: getJobHours(job),
    }));
}

/**
 * Distinct technician ids assigned to a card, header-first then in
 * first-seen order across its jobs — the primitive the card face's
 * multi-technician badge renders from.
 *
 * card.technicianId is UNIONED with the job-derived ids, not used as a
 * fallback-only-when-empty: the header tech is a real, independently-
 * meaningful assignment that coexists with job techs (it also drives
 * container placement — see repositories/cards.ts — and is written back
 * independently of job data by the WEBV3-241 technician write-back in
 * integrations/src/card/moveCard.ts), not a stand-in for when job techs are
 * absent.
 *
 * NOTE: this does not filter job-derived ids by `job.authorized` the way
 * Tekmetric's deriveTechnicianLaborFields does (tekmetric/src/converter/
 * index.ts:154) — a technician whose only job on the card is declined still
 * gets a badge here. Shopware's convertCard has no such filter either, so
 * the two integrations aren't filter-consistent with each other; this
 * helper intentionally matches neither exactly, it just unions every
 * job-level technicianId regardless of job state (via
 * getEmployeeJobEntriesFromCard, which has no authorized filter). Whether
 * declined-job technicians should be excluded from the badge row is an
 * open product question, not decided here.
 *
 * The header tech is pushed first so it survives the visible-badge cap
 * instead of being pushed into "+N" overflow, since it's the tech the
 * card's own placement is keyed on.
 */
export function getUniqueTechnicianIdsFromCard(card: Card): string[] {
  const seen = new Set<string>();
  const ids: string[] = [];
  const addId = (id: string | null | undefined) => {
    if (id && !seen.has(id)) {
      seen.add(id);
      ids.push(id);
    }
  };
  addId(card.technicianId);
  for (const entry of getEmployeeJobEntriesFromCard(card)) {
    addId(entry.employeeId);
  }
  return ids;
}

/**
 * Sums a set of JobEntry hours by state — the shared reduction behind both
 * the parent table's per-technician aggregation
 * (calculateEmployeeHoursByStateFromCard in dispatchCalculations.ts) and
 * the per-employee drill-down's status bar (EmployeeJobsDetail.tsx), so the
 * two can't drift on how a state's hours are accumulated.
 */
export function sumHoursByState(entries: JobEntry[]): Record<JobState, number> {
  const hours: Record<JobState, number> = {
    pending: 0,
    approved: 0,
    completed: 0,
    declined: 0,
  };
  for (const entry of entries) {
    hours[entry.state] += entry.hours;
  }
  return hours;
}
